import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  UserScheduledReport,
  CreateUserScheduledReportPayload,
  UpdateUserScheduledReportPayload,
} from '@/types'
import { toast } from 'sonner'

const CLIENT_REPORTS_QUERY_KEY = 'client-reports'

export const useClientReports = (userId: string) => {
  return useQuery<UserScheduledReport[]>({
    queryKey: [CLIENT_REPORTS_QUERY_KEY, userId],
    queryFn: async () => {
      const { data } = await api.get(`/users/${userId}/scheduled-reports`)
      return data
    },
    enabled: !!userId,
  })
}

export const useCreateClientReport = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      payload,
      enforceLimits = true,
    }: {
      payload: CreateUserScheduledReportPayload
      /** false = staff override, bypasses the user's plan gates/limits (card 92). */
      enforceLimits?: boolean
    }) => {
      const { data } = await api.post<UserScheduledReport>(
        `/users/${userId}/scheduled-reports?enforce_plan_limits=${enforceLimits}`,
        payload
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_REPORTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Reporte programado creado')
    },
  })
}

export const useUpdateClientReport = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string
      payload: UpdateUserScheduledReportPayload
    }) => {
      const { data } = await api.patch<UserScheduledReport>(
        `/users/${userId}/scheduled-reports/${id}`,
        payload
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_REPORTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Reporte programado actualizado')
    },
  })
}

export const useDeleteClientReport = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${userId}/scheduled-reports/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_REPORTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Reporte programado eliminado')
    },
  })
}

export const useToggleClientReport = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, pause }: { id: string; pause: boolean }) => {
      const { data } = await api.patch<UserScheduledReport>(
        `/users/${userId}/scheduled-reports/${id}/${pause ? 'pause' : 'resume'}`
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_REPORTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Estado del reporte actualizado')
    },
  })
}
